import type { VoidFunctionComponent } from 'react';
import { icons, IconButton } from '../../../../generic';
import Tooltip from '../../../../generic/Tooltip';
import { useDeleteTeamGroup } from '../../../../../hooks/api';
import { useAddToast } from '../../../../../contexts/ToastContext';
import { useOnConfirm } from '../../../../../contexts/DialogContext';
import { useRouter } from '../../../../../contexts/RouterContext';
import { getErrorMessage } from '../../../../../ReactQuery';
import { Action, ResourceType, useHasPermission } from '../../../../../shared/permissions';
import styles from './TeamGroups.module.less';

type Props = {
  teamGroupId: number;
  teamGroupName: string;
};

/**
 * Component that renders the action buttons for a team group table row
 * Includes edit and delete functionality.
 */
const TeamGroupsTableRowActions: VoidFunctionComponent<Props> = ({
  teamGroupId,
  teamGroupName,
}) => {
  const addToast = useAddToast();
  const onConfirm = useOnConfirm();
  const router = useRouter();
  const { mutateAsync: deleteTeamGroup, isLoading: isDeleting } = useDeleteTeamGroup(teamGroupId);
  const hasPermission = useHasPermission();

  //  Note: there is no delete permission for teams, so we use the create permission instead (ie: if you can create a team, you can delete a team group)
  const canDeleteTeamGroup = hasPermission({ resourceType: ResourceType.Team, action: Action.Create });

  const handleDeleteTeamGroup = async () => {
    try {
      await deleteTeamGroup();
      addToast({
        type: 'success',
        content: `The team group "${teamGroupName}" has been deleted successfully.`,
      });
    } catch (error: unknown) {
      addToast({
        type: 'error',
        content: getErrorMessage(error, `Failed to delete team group "${teamGroupName}".`),
      });
    }
  };

  const onDeleteTeamGroup = () =>
    onConfirm({
      title: 'Delete Team Group',
      body: `Are you sure you want to delete the team group "${teamGroupName}"?`,
      onConfirm: handleDeleteTeamGroup,
      confirmText: 'Delete',
      confirmBtnType: 'error',
    });

  return (
    <span className={styles.tableRowActions}>
      <IconButton
        id="edit-btn"
        icon={icons.edit}
        onClick={() => router.push(`/account/settings/organization/teams/groups/${teamGroupId}`)}
        isDisabled={isDeleting}
      />
      <Tooltip
        id={`delete-team-group-btn-${teamGroupId}`}
        showTooltip={!canDeleteTeamGroup}
        tooltip="You do not have permission to delete this team group."
        placement="left"
      >
        <span className="inline-block">
          <IconButton
            id="delete-btn"
            icon={icons.trashRegular}
            onClick={onDeleteTeamGroup}
            isDisabled={isDeleting || !canDeleteTeamGroup}
          />
        </span>
      </Tooltip>
    </span>
  );
};

export default TeamGroupsTableRowActions;
